/* eslint-disable react-hooks/exhaustive-deps */
import { Search, RotateCw } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import { WorkOrderStatus } from '../../../components/atoms/StatusLabel'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import api from '../../../config/AxiosInstance'
import { convertDateWithTime } from '../../../helpers/constants'
import toast, { Toaster } from 'react-hot-toast'
import '../../../styles/style.css'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const SelectStyles = {
  control: (base, state) => ({
    ...base,
    height: 45,
    minHeight: 45,
    borderRadius: '10px',
    borderColor: state.isFocused ? 'var(--primary-orange)' : '#e9ecef',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(248, 157, 29, 0.1)' : 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    fontSize: '0.9rem'
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    height: '45px',
    padding: '0 12px'
  }),
  input: (provided, state) => ({
    ...provided,
    margin: '0px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#495057'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? 'var(--primary-orange)'
      : state.isFocused
        ? '#fff5e6'
        : null,
    color: state.isSelected ? 'white' : '#333',
    cursor: 'pointer'
  })
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const STATUSES = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'unsubmitted', label: 'Unsubmitted' },
  { value: 'done', label: 'Done' }
]

const WorkOrderRepack = () => {
  const navigate = useNavigate()
  const { state } = useLocation()

  const [workOrder, setWorkOrder] = useState([])
  const [warehouses, setWarehouses] = useState([])
  const [loading, setLoading] = useState(false)

  const [q, setQ] = useState(state?.q || '')
  const [debouncedQ, setDebouncedQ] = useState(q)
  const [warehouseId, setWarehouseId] = useState(state?.warehouse_id || '')
  const [status, setStatus] = useState(state?.status || '')

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const refSrcWarehouses = useRef()
  const refSrcStatus = useRef()

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQ(q)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [q])

  const renderType = (type) => {
    let text = '-'
    switch (type) {
      case 'repack':
        text = 'Repack'
        break
      case 'extend':
        text = 'Extend'
        break
      case 'repack_extend':
        text = 'Repack and Extend'
        break
      default:
        break
    }
    return text
  }

  const columns = [
    {
      key: 'code',
      label: '#Work Order'
    },
    {
      key: 'request_time',
      label: 'Request Time',
      className: 'text-end',
      component: (value) =>
        value.request_date
          ? convertDateWithTime(value.request_date, 'DD-MM-YYYY, HH:mm')
          : '-'
    },
    {
      key: 'related_ticket',
      label: 'Related Ticket',
      className: 'text-end',
      component: (value) => (
        <div key={value.code} className="d-flex justify-content-end">
          {(!value?.related_ticket ||
            !value?.related_ticket?.length ||
            value?.related_ticket === '-') &&
            '-'}
          {typeof value?.related_ticket === 'string' && (
            <Link
              to={`/workorder/job-status/detail/${value?.job_id}`}
              className="text-decoration-none text-primary fw-bold"
            >
              {value?.related_ticket}
            </Link>
          )}
          {value?.related_ticket &&
            Array.isArray(value?.related_ticket) &&
            value?.related_ticket?.length &&
            value?.related_ticket !== '-' && (
              <ul className="list-unstyled">
                {value?.related_ticket?.map((ticket, index) => (
                  <li key={index} className="mb-1">
                    <Link
                      to={`/workorder/job-status/detail/${value?.job_id}`}
                      className="text-decoration-none text-primary fw-bold"
                    >
                      {ticket.code}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
        </div>
      )
    },
    {
      key: 'erp_doc',
      label: 'ERP Doc. No.',
      className: 'text-end',
      component: (value) => value.erp_doc || '-'
    },
    {
      key: 'type',
      label: 'Type',
      className: 'text-end',
      component: (value) => renderType(value.type)
    },
    {
      key: 'warehouse',
      label: 'Warehouse',
      className: 'text-end'
    },
    {
      key: 'status',
      label: 'Status',
      className: 'text-end',
      component: (value) => <WorkOrderStatus text={value.status} />
    },
    {
      key: 'sent_erp',
      label: 'Sent to ERP',
      className: 'text-end',
      component: (value) =>
        value.sent_erp
          ? convertDateWithTime(value.sent_erp, 'DD-MM-YYYY, HH:mm')
          : '-'
    },
    {
      key: 'action',
      label: 'Action',
      className: 'text-end',
      component: (value) => (
        <div key={value.code} className="d-flex justify-content-end">
          <button
            className="btn btn-light text-secondary border rounded"
            style={{ width: '32px', height: '32px', padding: 0 }}
            onClick={() =>
              navigate(
                `/workorder/repack/detail/${encodeURIComponent(value.code)}`,
                {
                  state: {
                    q,
                    warehouse_id: warehouseId,
                    status
                  }
                }
              )
            }
          >
            <Search size={14} />
          </button>
        </div>
      )
    }
  ]

  const fetchListWorkOrder = async (page = null) => {
    try {
      setLoading(true)
      const res = await api.get('/outbound/work-order-repack', {
        params: {
          per_page: meta.per_page,
          page: page ?? meta.current_page,
          q: debouncedQ,
          warehouse_id: warehouseId,
          status: status
        }
      })
      setWorkOrder(res.data.data)
      setMeta(res.data.metadata)
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        err?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchWarehouse = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const options = data.map((w) => ({
        value: w.id,
        label: w.warehouse_name
      }))
      setWarehouses(options)
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        err?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  useEffect(() => {
    fetchListWorkOrder()
  }, [meta.per_page, meta.current_page, debouncedQ, warehouseId, status])

  useEffect(() => {
    if (!warehouses.length) fetchWarehouse()
  }, [])

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handleReset = () => {
    setQ('')
    setDebouncedQ('')
    setWarehouseId('')
    setStatus('')
    refSrcWarehouses.current?.clearValue()
    refSrcStatus.current?.clearValue()
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  return (
    <>
      <Toaster />
      <Container fluid className="px-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Work Order Plan Repack</h3>
          </Col>
          <Col className="text-end">
            <Button
              variant="light"
              className="text-secondary fw-bold rounded px-3 border"
              onClick={handleReset}
              style={{ fontSize: '0.85rem' }}
            >
              <RotateCw size={14} className="me-2" />
              Reset Filters
            </Button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Work Order</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search size={14} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search Work Order Code"
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                    onChange={(e) => {
                      setQ(e.target.value)
                    }}
                    value={q}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Warehouse</Form.Label>
                <Select
                  isClearable
                  ref={refSrcWarehouses}
                  options={warehouses}
                  styles={SelectStyles}
                  placeholder="All Warehouses"
                  onChange={(e) => {
                    setWarehouseId(e?.value || '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  value={
                    warehouseId
                      ? warehouses.find((w) => w.value === warehouseId)
                      : null
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={labelStyle}>Status</Form.Label>
                <Select
                  isClearable
                  ref={refSrcStatus}
                  options={STATUSES}
                  styles={SelectStyles}
                  placeholder="All Statuses"
                  onChange={(e) => {
                    setStatus(e?.value || '')
                    setMeta((prev) => ({ ...prev, current_page: 1 }))
                  }}
                  value={
                    status ? STATUSES.find((s) => s.value === status) : null
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row>
          <Col md={12}>
            <Table
              hover
              responsive
              className="shadow-sm mb-5 bg-white"
              style={{
                borderRadius: '12px',
                overflow: 'hidden',
                fontSize: '0.9rem'
              }}
            >
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      className={`p-2 align-middle ${column.className || ''}`}
                      key={column.key}
                      style={tableHeaderStyle}
                    >
                      <span>{column.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={columns.length} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!loading && workOrder.length === 0 && (
                  <RowEmpty colSpan={columns.length} />
                )}
                {!loading &&
                  workOrder.length > 0 &&
                  workOrder?.map((repack) => (
                    <tr key={repack.code}>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`align-middle p-2 ${column.className || ''}`}
                        >
                          {column.component && column.component(repack)}
                          {!column.component && repack[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={columns.length} className="p-3 border-top-0">
                    <TablePagination
                      meta={meta}
                      handleOnChangeMeta={handleOnChangeMeta}
                      handlePageChange={handlePageChange}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default WorkOrderRepack
